// Action d'une fiche des finances avec champs typés (texte, texte long, nombre, date, choix,
// case) : fenêtre de confirmation, appel réel de la route, succès seulement après la réponse du
// serveur, erreurs par champ (400) et message d'erreur fonctionnel (403 quatre yeux, 409...).
import { Bandeau, Bouton, CaseACocher, Champ, DateChamp, DialogueConfirmation, Selection, ZoneTexte, type Icone, type OptionSelection, type VarianteBouton } from '@cc/ui';
import { useState, type ReactNode } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { INVALIDER_RECETTES } from './requetes';

export interface ChampFormulaireAction {
  nom: string;
  libelle: string;
  aide?: string;
  obligatoire?: boolean;
  type?: 'texte' | 'multiligne' | 'nombre' | 'date' | 'selection' | 'case';
  options?: OptionSelection[];
  defaut?: string | boolean;
}

export interface ProprietesFormulaireAction<R> {
  libelle: string;
  titre: string;
  explication: ReactNode;
  chemin: string;
  methode?: 'POST' | 'PATCH';
  champs?: ChampFormulaireAction[];
  /** Corps fixe ajouté aux champs saisis. */
  corps?: Record<string, unknown>;
  succes: string | ((r: R) => string);
  invalider?: readonly (readonly unknown[])[];
  variante?: VarianteBouton;
  icone?: Icone;
  petit?: boolean;
  desactive?: boolean;
  apres?: (r: R) => void;
  children?: ReactNode;
}

type Valeurs = Record<string, string | boolean>;

function valeursInitiales(champs: ChampFormulaireAction[]): Valeurs {
  return Object.fromEntries(champs.map((c) => [c.nom, c.defaut ?? (c.type === 'case' ? false : '')]));
}

/** Corps envoyé : nombres convertis, cases en booléens, champs vides omis. */
export function corpsAction(champs: ChampFormulaireAction[], valeurs: Valeurs, fixe: Record<string, unknown> = {}): Record<string, unknown> {
  const corps: Record<string, unknown> = { ...fixe };
  for (const c of champs) {
    const v = valeurs[c.nom];
    if (c.type === 'case') corps[c.nom] = Boolean(v);
    else if (typeof v === 'string' && v.trim() !== '') corps[c.nom] = c.type === 'nombre' ? Number(v.replace(/\s/g, '').replace(',', '.')) : v.trim();
  }
  return corps;
}

export function incomplet(champs: ChampFormulaireAction[], valeurs: Valeurs): boolean {
  return champs.some((c) => {
    const v = valeurs[c.nom];
    if (c.obligatoire && (typeof v !== 'string' || v.trim() === '')) return true;
    if (c.type === 'nombre' && typeof v === 'string' && v.trim() !== '' && Number.isNaN(Number(v.replace(/\s/g, '').replace(',', '.')))) return true;
    return false;
  });
}

export function FormulaireAction<R = unknown>({ libelle, titre, explication, chemin, methode = 'POST', champs = [], corps = {}, succes, invalider = INVALIDER_RECETTES, variante = 'principal', icone, petit, desactive, apres, children }: ProprietesFormulaireAction<R>) {
  const [ouvert, setOuvert] = useState(false);
  const [valeurs, setValeurs] = useState<Valeurs>(() => valeursInitiales(champs));
  const action = useAction<undefined, R>({
    executer: () => requete<R>(chemin, { methode, corps: corpsAction(champs, valeurs, corps) }),
    succes,
    invalider,
    apres: (r) => {
      setOuvert(false);
      setValeurs(valeursInitiales(champs));
      apres?.(r);
    },
  });
  const erreur = (nom: string) => action.erreursChamps.find((x) => x.champ === nom || x.champ.startsWith(`${nom}.`))?.message;
  const changer = (nom: string) => (e: { target: { value: string } }) => setValeurs((v) => ({ ...v, [nom]: e.target.value }));
  return (
    <>
      <Bouton variante={variante} icone={icone} petit={petit} disabled={desactive} onClick={() => setOuvert(true)}>
        {libelle}
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={titre}
        message={typeof explication === 'string' ? <p>{explication}</p> : explication}
        libelleConfirmer={libelle}
        variante={variante}
        enCours={action.enCours}
        confirmationDesactivee={incomplet(champs, valeurs)}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        {children}
        {champs.map((c) => {
          const id = `action-${c.nom}`;
          const v = valeurs[c.nom];
          const texte = typeof v === 'string' ? v : '';
          if (c.type === 'selection') return <Selection key={c.nom} id={id} libelle={c.libelle} aide={c.aide} required={c.obligatoire} facultatif={!c.obligatoire} optionVide="Choisir" options={c.options ?? []} value={texte} onChange={changer(c.nom)} erreur={erreur(c.nom)} />;
          if (c.type === 'multiligne') return <ZoneTexte key={c.nom} id={id} libelle={c.libelle} aide={c.aide} required={c.obligatoire} facultatif={!c.obligatoire} value={texte} onChange={changer(c.nom)} erreur={erreur(c.nom)} />;
          if (c.type === 'date') return <DateChamp key={c.nom} id={id} libelle={c.libelle} aide={c.aide} required={c.obligatoire} facultatif={!c.obligatoire} value={texte} onChange={changer(c.nom)} erreur={erreur(c.nom)} />;
          if (c.type === 'case') return <CaseACocher key={c.nom} id={id} libelle={c.libelle} checked={Boolean(v)} onChange={(e) => setValeurs((x) => ({ ...x, [c.nom]: e.target.checked }))} />;
          return <Champ key={c.nom} id={id} libelle={c.libelle} aide={c.aide} required={c.obligatoire} facultatif={!c.obligatoire} inputMode={c.type === 'nombre' ? 'numeric' : undefined} value={texte} onChange={changer(c.nom)} erreur={erreur(c.nom)} />;
        })}
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}
